// Thin cookie-authed client for share viewer + visibility endpoints. Every call
// sends credentials:'include' (the worker resolves the bw_session cookie).
// Returns a discriminated result so callers render specific errors.

export type ApiResult<T = Record<string, never>> = ({ ok: true } & T) | { ok: false; error: string };

async function call<T>(url: string, init: RequestInit, fetchImpl: typeof fetch): Promise<ApiResult<T>> {
  try {
    const res = await fetchImpl(url, { ...init, credentials: 'include' });
    const body = await res.json().catch(() => ({}));
    if (res.ok) return { ok: true, ...(body as T) };
    return { ok: false, error: (body as { error?: string }).error ?? `http_${res.status}` };
  } catch {
    return { ok: false, error: 'network' };
  }
}

export interface ViewerInfo {
  userId: string;
  email: string;
  grantedAt: string;
  lastSeenAt: string | null;
  viewCount: number;
  source: 'team' | 'grant';
  revokedAt: string | null;
}

/**
 * GET /api/shares/:shareId/viewers — returns raw array from worker.
 * We do NOT use the shared `call` helper's spread directly because the worker
 * returns a raw JSON array, not {viewers: [...]}.
 */
export async function listViewers(
  shareId: string,
  apiBase = '',
  fetchImpl: typeof fetch = fetch,
): Promise<ApiResult<{ viewers: ViewerInfo[] }>> {
  try {
    const res = await fetchImpl(`${apiBase}/api/shares/${shareId}/viewers`, {
      method: 'GET',
      credentials: 'include',
    });
    const body = await res.json().catch(() => ({}));
    if (res.ok) {
      return { ok: true, viewers: body as ViewerInfo[] };
    }
    return { ok: false, error: (body as { error?: string }).error ?? `http_${res.status}` };
  } catch {
    return { ok: false, error: 'network' };
  }
}

export interface OwnedShare {
  id: string;
  songName: string | null;
  visibility: 'public' | 'team';
  sizeBytes: number | null;
  createdAt: string;
}

/**
 * GET /api/my-shares — the signed-in user's own published versions, including
 * each version's current `visibility`. Like listViewers, the worker returns a
 * raw JSON array (not wrapped). Used to pre-select the current visibility in the
 * owner's Viewers panel.
 */
export async function listMyShares(
  apiBase = '',
  fetchImpl: typeof fetch = fetch,
): Promise<ApiResult<{ shares: OwnedShare[] }>> {
  try {
    const res = await fetchImpl(`${apiBase}/api/my-shares`, { method: 'GET', credentials: 'include' });
    const body = await res.json().catch(() => ([]));
    if (res.ok) return { ok: true, shares: body as OwnedShare[] };
    return { ok: false, error: (body as { error?: string }).error ?? `http_${res.status}` };
  } catch {
    return { ok: false, error: 'network' };
  }
}

/** DELETE /api/shares/:shareId/viewers/:userId */
export function revokeViewer(
  shareId: string,
  userId: string,
  apiBase = '',
  fetchImpl: typeof fetch = fetch,
): Promise<ApiResult> {
  return call(`${apiBase}/api/shares/${shareId}/viewers/${userId}`, { method: 'DELETE' }, fetchImpl);
}

/**
 * PATCH /api/shares/:shareId body { visibility, teamId? }
 * teamId is only included in the body when visibility === 'team'.
 */
export function setVisibility(
  shareId: string,
  visibility: 'public' | 'team',
  teamId: string | null,
  apiBase = '',
  fetchImpl: typeof fetch = fetch,
): Promise<ApiResult<{ visibility: string; teamId: string | null }>> {
  const body: Record<string, string> = { visibility };
  if (visibility === 'team' && teamId !== null) {
    body.teamId = teamId;
  }
  return call(
    `${apiBase}/api/shares/${shareId}`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    },
    fetchImpl,
  );
}

/**
 * Лестница отказов у снятия КОРОЧЕ, чем была у пометки главной (маршрут
 * `PATCH /api/shares/:id/default` — метка владельца, которую больше не читает
 * веб, единый сетлист, фаза 4, `TD-260924-song-default-route-until-phase-5`),
 * и список причин это отражает: маршрут `DELETE /api/shares/:id` отвечает
 * 401 → 404 → 403 → 200 и ничем больше. `revoked` и `noSong` сюда не
 * переезжают — причина, которой маршрут не выдаёт, в перечислении означала бы
 * мёртвую ветку в каждом, кто его разбирает.
 */
export type ShareRemoveFail = 'auth' | 'forbidden' | 'gone' | 'network';
export type ShareRemoveResult = { ok: true } | { ok: false; reason: ShareRemoveFail };

/**
 * DELETE /api/shares/:shareId — владелец снимает свою запись с публикации.
 *
 * Тот же адрес, которым пользуется десктоп, но ДРУГОЕ доказательство права:
 * десктоп шлёт `Authorization: Bearer <SHARE_TOKEN>` плюс `X-User-Token`, а
 * здесь едет сессионная cookie и больше ничего. `SHARE_TOKEN` одинаков во всех
 * установках, и отдать его браузеру значит раздать право удаления всем;
 * маршрут поэтому различает пути по наличию заголовка `Authorization` —
 * `functions/api/shares/[shareId].ts`.
 *
 * Причина выводится из СТАТУСА, как у `setSongDefault` выше и у соседнего
 * `song-text-api.ts`: у 5xx и у промежуточных прокси тела может не быть вовсе,
 * и «http_502» из тела уехало бы человеку на экран как есть.
 */
export async function removeShare(
  shareId: string,
  apiBase = '',
  fetchImpl: typeof fetch = fetch,
): Promise<ShareRemoveResult> {
  try {
    const res = await fetchImpl(`${apiBase}/api/shares/${encodeURIComponent(shareId)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    const status = res.status;
    if (status === 401) return { ok: false, reason: 'auth' };
    if (status === 403) return { ok: false, reason: 'forbidden' };
    if (status === 404) return { ok: false, reason: 'gone' };
    if (status < 200 || status >= 300) return { ok: false, reason: 'network' };
    return { ok: true };
  } catch {
    return { ok: false, reason: 'network' };
  }
}
